import _ from "lodash";
import React, { PureComponent, ReactNode } from "react";
import { BaseTable, ViewMode } from "../../base-table";

declare global {
  interface Window {
    kuika: any;
    nextCellToFocus: {
      tableId: string;
      columnIndex: number;
      rowIndex: string;
    } | null;
    handlingCellClick?: boolean;
  }
}

interface TableBodyCellProps {
  children?: ReactNode;
  calculatedCellBgColor?: (rowIndex?: number, rowData?: any) => string;
  calculatedCellFontColor?: (rowIndex?: number, rowData?: any) => string;
  style?: any;
  rowData?: any;
  rowIndex?: number;
  columnIndex: number;
  title: string;
  mode?: ViewMode;
  onCellClick: (e: any, columnIndex?: number, rowData?: any, rowIndex?: number) => void;
  onCellBlur: (e: any) => void;
  isEditable?: boolean;
  isEditMode?: boolean;
  dataValue?: any;
  editableAlwaysActive?: boolean;
  setResizingColumnIndex?: (index: number) => void;
  resizeListener?: (e: MouseEvent) => void;
  index?: number;
  resizableColumns?: boolean;
  tableId?: string;
  parentTable?: BaseTable<any, any>;
  fixed?: "left" | "right";
  left?: number;
  right?: number;
}
interface TableBodyCellState {}

export class TableBodyCell extends PureComponent<TableBodyCellProps, TableBodyCellState> {
  constructor(props: TableBodyCellProps) {
    super(props);
  }

  getStyle = (): React.CSSProperties => {
    let style: React.CSSProperties = {};
    if (this.props.style) {
      style = _.clone(this.props.style);
    }
    if (this.props.calculatedCellBgColor) {
      style.background = this.props.calculatedCellBgColor(this.props.rowIndex, this.props.rowData);
    }
    if (this.props.calculatedCellFontColor) {
      style.color = this.props.calculatedCellFontColor(this.props.rowIndex, this.props.rowData);
    }
    if (
      !style.border &&
      !style.borderColor &&
      !style.borderTop &&
      !style.borderBottom &&
      !style.borderRight &&
      !style.borderLeft &&
      !style.borderWidth
    ) {
      style.borderWidth = 0;
    }
    if (style?.display) {
      delete style.display;
    }
    if (!style?.padding) {
      style.padding = "0px";
    }
    if (style.padding && (style?.paddingLeft || style?.paddingRight || style?.paddingTop || style?.paddingBottom)) {
      delete style.padding;
    }
    if (window.kuika.dashboardState === 17 && style.alignItems) {
      style.alignContent = style.alignItems;
    }
    if (this.props.fixed === "left" && this.props.left && !window.kuika.isDesignTime) {
      style.left = this.props.left;
    }
    if (this.props.fixed === "right" && this.props.right && !window.kuika.isDesignTime) {
      style.right = this.props.right;
    }
    return style;
  };

  handleCellClick = (e: any) => {
    if (this.props.onCellClick) {
      this.props.onCellClick(e);
    }
  };

  handleCellBlur = (e: any) => {
    if (this.props.onCellBlur) {
      this.props.onCellBlur(e);
    }
  };

  filterNodeByKuikaComponentName(node) {
    if (!node || !node.props) {
      return true;
    }
    const { props } = node;

    if (props && props.kuikacomponentname != "Label") {
      return node;
    }

    if (props && props.children) {
      const filteredChildren = React.Children.map(props.children, (child) =>
        this.filterNodeByKuikaComponentName(child)
      );

      const hasFilteredChildren = filteredChildren.some((child) => child !== null);
      if (hasFilteredChildren) {
        return {
          ...node,
          props: {
            ...props,
            children: filteredChildren
          }
        };
      }
    }

    return null;
  }

  componentDidMount(): void {
    if (
      this.props.editableAlwaysActive &&
      this.props.columnIndex?.toString() === window.nextCellToFocus?.columnIndex?.toString() &&
      this.props.rowIndex?.toString() === window.nextCellToFocus?.rowIndex?.toString()
    ) {
      setTimeout(() => {
        const cellElement = this.getThisCellElement();
        if (cellElement) {
          let inputElement = cellElement.querySelector("input") as HTMLInputElement;
          if (inputElement && document.activeElement !== inputElement) {
            if (this.props.columnIndex !== undefined && this.props.rowIndex !== undefined) {
              inputElement.focus();

              if (this.props.onCellClick && !window.handlingCellClick) {
                window.handlingCellClick = true;

                const syntheticEvent = {
                  currentTarget: cellElement,
                  target: inputElement,
                  preventDefault: () => {},
                  stopPropagation: () => {}
                };
                this.props.onCellClick(syntheticEvent, this.props.columnIndex, this.props.rowData, this.props.rowIndex);
                setTimeout(() => {
                  window.handlingCellClick = false;
                  window.nextCellToFocus = null;
                }, 500);
              }
            }
          }
        }
      }, 500);
    }

    if (window.nextCellToFocus === undefined) {
      window.nextCellToFocus = null;
    }
  }

  getThisCellElement = () => {
    let cellElement: HTMLElement | null = null;
    const tableId = this.props.tableId;
    if (!tableId) {
      return null;
    }
    const tableElement = window.document.querySelector(`[id="${tableId}"]`) as HTMLElement;
    if (!tableElement) {
      return null;
    }
    const rowWithId = tableElement.querySelector(`tr[data-row-key="${this.props.rowData?.id}"]`) as HTMLElement;

    if (rowWithId) {
      cellElement = rowWithId.querySelector(`td[columnindex="${this.props.columnIndex}"]`) as HTMLElement;
    }

    if (!cellElement) {
      const tableRows = tableElement.querySelectorAll("tr");

      for (const row of tableRows) {
        const rowDataAttr = row.getAttribute("data-row-key");
        const rowIndexAttr = row.getAttribute("rowindex");

        if (
          (rowDataAttr && rowDataAttr === this.props.rowData?.id?.toString()) ||
          (rowIndexAttr && rowIndexAttr === this.props.rowIndex?.toString() && this.props.rowIndex !== undefined)
        ) {
          cellElement = row.querySelector(`td[columnindex="${this.props.columnIndex}"]`) as HTMLElement;
          break;
        }
      }
    }

    return cellElement;
  };

  getProps = () => {
    const props: any = _.clone(this.props);
    props.tableTitle = this.props.title;
    props.dataValue = this.props.dataValue;
    props.title = "";
    const screenWidth = window.innerWidth;
    if (this.props.resizableColumns && !window.kuika.isDesignTime && screenWidth > 1024) {
      delete props.children;
    }
    return props;
  };

  render(): ReactNode {
    const screenWidth = window.innerWidth;

    if (this.props.resizableColumns && !window.kuika.isDesignTime && screenWidth > 1024) {
      return (
        <td
          onClickCapture={this.handleCellClick}
          {...this.getProps()}
          style={this.getStyle()}
          onBlur={this.handleCellBlur}
          tabIndex={this.props.editableAlwaysActive ? 0 : undefined}
        >
          <div
            className="resizeHandle"
            onMouseDown={() => {
              if (this.props.setResizingColumnIndex && this.props.index !== undefined) {
                this.props.setResizingColumnIndex(this.props.index);
                window.addEventListener("mousemove", this.props.resizeListener, { passive: true });
              }
            }}
          />
          {this.props.children}
        </td>
      );
    }
    return (
      <td
        onClickCapture={this.handleCellClick}
        {...this.getProps()}
        style={this.getStyle()}
        onBlur={this.handleCellBlur}
        tabIndex={this.props.editableAlwaysActive ? 0 : undefined}
      />
    );
  }
}
